import { useState, useMemo } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Eye,
  BarChart3,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Stethoscope,
  Search,
  Columns,
  List,
  AlertCircle,
  Home,
  Repeat,
  TrendingUp,
  Layers,
  Play,
  ClipboardCheck,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import { format } from 'date-fns';
import { LoadingState, ErrorState, Breadcrumb } from '../../components/ui';
import { Phase2ConsistencyMetrics } from './components/ConsistencyMetrics';
import type { Phase1Metrics, Phase2Metrics } from './components/ConsistencyMetrics';
import { LLMConsistencyAnalysis } from './components/LLMConsistencyAnalysis';

interface SearchExecution {
  id: string;
  searchModel: string;
  blindCode: string;
  status: string;
  searchResults?: {
    webResults?: Array<{
      doctorId?: string;
      name: string;
      title?: string;
      institution?: string;
      city?: string;
      canton?: string;
      specialty?: string;
      matchScore?: number;
    }>;
    searchSummary?: string;
  };
  latencyMs?: number;
  costUsd?: number;
  autoScore?: {
    relevanceScore?: number;
    geographicScore?: number;
    specialtyMatchScore?: number;
    combinedScore?: number;
  };
}

interface Execution {
  id: string;
  model: string;
  blindCode: string;
  status: string;
  iteration?: number;
  modelVersion?: string;
  selectedForSearch?: boolean;
  searchExecutions?: SearchExecution[];
  indication?: {
    suitability: string;
    confidence: number;
    summary: string;
    issues: Array<{ severity: string; description: string; recommendation?: string }>;
    patientExpectations?: {
      summary: string;
      concerns: string[];
      goals: string[];
    };
  };
  criteria?: {
    criteria: Array<{
      id: string;
      field: string;
      value: string | string[];
      priority: string;
      reasoning: string;
    }>;
    priorityExplanation?: string;
  };
  indicationLatencyMs?: number;
  criteriaLatencyMs?: number;
  indicationCostUsd?: number;
  criteriaCostUsd?: number;
  autoScore?: {
    combinedScore: number;
    suitabilityScore: number;
    confidenceScore: number;
  };
}

interface StabilityEvaluation {
  id: string;
  phase: 'PHASE_1' | 'PHASE_2';
  verdict: string;
  verdictReasoning?: string;
  observations?: string;
  keyDifferences?: string[];
  consistencyMetrics?: Phase1Metrics | Phase2Metrics;
  evaluatorConfidence?: number;
  evaluatorNotes?: string;
  evaluator: { id: string; name: string; email: string };
  completedAt?: string;
  createdAt: string;
}

interface TestRun {
  id: string;
  name: string;
  status: string;
  runType: string;
  isRevealed: boolean;
  startedAt?: string;
  completedAt?: string;
  searchPhaseStatus?: 'NOT_STARTED' | 'READY' | 'RUNNING' | 'COMPLETED' | 'EVALUATED';
  config: {
    models: string[];
    iterations: number;
  };
  testCase: {
    id: string;
    name: string;
    code: string;
    condition: string;
  };
  executions: Execution[];
  stabilityEvaluations: StabilityEvaluation[];
}

const MODEL_DISPLAY: Record<string, { name: string; color: string }> = {
  CLAUDE_OPUS_4_5: { name: 'Claude Opus 4.5', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  CLAUDE_OPUS_4_6: { name: 'Claude Opus 4.6', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  GPT_5_2_PRO: { name: 'GPT-5.2 Pro', color: 'bg-green-100 text-green-700 border-green-200' },
  GEMINI_3_PRO: { name: 'Gemini 3 Pro', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  GRAND_ROUNDS: { name: 'Grand Rounds', color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const STABILITY_VERDICT_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string; bgColor: string }> = {
  HIGHLY_STABLE: { label: 'Highly Stable', icon: CheckCircle, color: 'text-green-700', bgColor: 'bg-green-100' },
  STABLE: { label: 'Stable', icon: CheckCircle, color: 'text-blue-700', bgColor: 'bg-blue-100' },
  MODERATE: { label: 'Moderate', icon: AlertTriangle, color: 'text-amber-700', bgColor: 'bg-amber-100' },
  UNSTABLE: { label: 'Unstable', icon: AlertCircle, color: 'text-orange-700', bgColor: 'bg-orange-100' },
  HIGHLY_UNSTABLE: { label: 'Highly Unstable', icon: XCircle, color: 'text-red-700', bgColor: 'bg-red-100' },
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-gray-100 text-gray-600 border-gray-200',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-600',
};

function ModelBadge({ model }: { model: string }) {
  const config = MODEL_DISPLAY[model] || { name: model, color: 'bg-gray-100 text-gray-700' };
  return (
    <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border', config.color)}>
      {config.name}
    </span>
  );
}

function StabilityVerdictBadge({ verdict }: { verdict: string }) {
  const config = STABILITY_VERDICT_CONFIG[verdict] || { label: verdict, icon: AlertTriangle, color: 'text-gray-700', bgColor: 'bg-gray-100' };
  const Icon = config.icon;
  return (
    <span className={clsx('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium', config.bgColor, config.color)}>
      <Icon className="h-4 w-4" />
      {config.label}
    </span>
  );
}

// Calculate Phase 2 metrics from search executions
function calculatePhase2Metrics(executions: Execution[]): Phase2Metrics {
  const allSearchExecutions = executions.flatMap(e => e.searchExecutions || [])
    .filter(se => se.status === 'COMPLETED');

  if (allSearchExecutions.length === 0) {
    return {
      providerOverlap: 0,
      top3Consistency: 0,
      top5Consistency: 0,
      rankingCorrelation: 0,
      scoreVariance: 0,
      uniqueProviders: 0,
      commonProviders: 0,
      totalIterations: 0,
    };
  }

  // Track providers across iterations
  const allProviders = new Map<string, Set<string>>();
  const iterationsWithResults = allSearchExecutions.filter(
    se => (se.searchResults?.webResults || []).length > 0
  );
  const iterationsWithResultsCount = iterationsWithResults.length;
  const effectiveIterations = Math.max(iterationsWithResultsCount, 1);

  allSearchExecutions.forEach(se => {
    const results = se.searchResults?.webResults || [];
    results.forEach(result => {
      const key = result.doctorId || `${result.name}-${result.institution || ''}`;
      if (!allProviders.has(key)) {
        allProviders.set(key, new Set());
      }
      allProviders.get(key)!.add(se.blindCode);
    });
  });

  const totalProviders = allProviders.size;
  const commonProviders = Array.from(allProviders.entries())
    .filter(([_, codes]) => codes.size >= effectiveIterations).length;
  const providerOverlap = totalProviders > 0 ? (commonProviders / totalProviders) * 100 : 100;

  // Top-3 consistency
  const top3ByExecution = iterationsWithResults.map(se => {
    const results = se.searchResults?.webResults || [];
    return results.slice(0, 3).map(r => r.doctorId || `${r.name}-${r.institution || ''}`);
  });

  let top3Consistency = 100;
  if (top3ByExecution.length > 0) {
    const top3Union = new Set(top3ByExecution.flat());
    const top3Intersection = Array.from(top3Union).filter(provider =>
      top3ByExecution.every(top3 => top3.includes(provider))
    );
    top3Consistency = top3Union.size > 0 ? (top3Intersection.length / Math.min(3, top3Union.size)) * 100 : 100;
  }

  // Top-5 consistency
  const top5ByExecution = iterationsWithResults.map(se => {
    const results = se.searchResults?.webResults || [];
    return results.slice(0, 5).map(r => r.doctorId || `${r.name}-${r.institution || ''}`);
  });

  let top5Consistency = 100;
  if (top5ByExecution.length > 0) {
    const top5Union = new Set(top5ByExecution.flat());
    const top5Intersection = Array.from(top5Union).filter(provider =>
      top5ByExecution.every(top5 => top5.includes(provider))
    );
    top5Consistency = top5Union.size > 0 ? (top5Intersection.length / Math.min(5, top5Union.size)) * 100 : 100;
  }

  const emptyIterationsCount = allSearchExecutions.length - iterationsWithResultsCount;

  return {
    providerOverlap,
    top3Consistency,
    top5Consistency,
    rankingCorrelation: 0.8, // Simplified
    scoreVariance: 100 - providerOverlap,
    uniqueProviders: totalProviders,
    commonProviders,
    totalIterations: allSearchExecutions.length,
    iterationsWithResults: iterationsWithResultsCount,
    emptyIterations: emptyIterationsCount,
  };
}

function IterationCard({ execution, index }: { execution: Execution; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const indication = execution.indication;
  const criteria = execution.criteria;

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Header */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-700 font-semibold text-sm">
              {index + 1}
            </div>
            <div>
              <span className="text-sm font-medium text-gray-900">Iteration {index + 1}</span>
              <span className="ml-2 text-xs text-gray-500">({execution.blindCode})</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {indication && (
              <span className={clsx(
                'px-2 py-1 rounded text-xs font-medium',
                indication.suitability === 'SUITABLE' ? 'bg-green-100 text-green-700' :
                indication.suitability === 'CONDITIONALLY_SUITABLE' ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              )}>
                {indication.suitability?.replace(/_/g, ' ')}
              </span>
            )}
            {indication && (
              <span className="text-sm text-gray-600">{indication.confidence}% conf</span>
            )}
            {expanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-gray-200 p-4 space-y-4">
          {/* Summary */}
          {indication?.summary && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1">Summary</h4>
              <p className="text-sm text-gray-600">{indication.summary}</p>
            </div>
          )}

          {/* Issues */}
          {indication?.issues && indication.issues.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Issues ({indication.issues.length})</h4>
              <div className="space-y-2">
                {indication.issues.map((issue, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className={clsx(
                      'px-1.5 py-0.5 rounded text-xs font-medium',
                      SEVERITY_COLORS[issue.severity?.toLowerCase()] || 'bg-gray-100 text-gray-600'
                    )}>
                      {issue.severity}
                    </span>
                    <span className="text-sm text-gray-600">{issue.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Criteria */}
          {criteria?.criteria && criteria.criteria.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Search Criteria ({criteria.criteria.length})</h4>
              <div className="flex flex-wrap gap-2">
                {criteria.criteria.map((c, idx) => (
                  <span
                    key={idx}
                    className={clsx(
                      'px-2 py-1 rounded text-xs font-medium border',
                      PRIORITY_COLORS[c.priority?.toLowerCase()] || 'bg-gray-100 text-gray-600'
                    )}
                  >
                    {c.field}: {Array.isArray(c.value) ? c.value.join(', ') : c.value}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Metrics */}
          <div className="flex items-center gap-6 text-xs text-gray-500 pt-2 border-t border-gray-100">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {((execution.indicationLatencyMs || 0) + (execution.criteriaLatencyMs || 0)) / 1000}s
            </span>
            <span className="flex items-center gap-1">
              <DollarSign className="h-3.5 w-3.5" />
              ${((execution.indicationCostUsd || 0) + (execution.criteriaCostUsd || 0)).toFixed(4)}
            </span>
            {execution.autoScore && (
              <span className="flex items-center gap-1">
                <BarChart3 className="h-3.5 w-3.5" />
                {Math.round(execution.autoScore.combinedScore)}% auto-score
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SearchIterationCard({ searchExecution, index }: { searchExecution: SearchExecution; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const results = searchExecution.searchResults?.webResults || [];

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Header */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm">
              {index + 1}
            </div>
            <div>
              <span className="text-sm font-medium text-gray-900">Search Iteration {index + 1}</span>
              <span className="ml-2 text-xs text-gray-500">({searchExecution.blindCode})</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">{results.length} providers</span>
            {expanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-gray-200 p-4">
          {results.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No providers found</p>
          ) : (
            <div className="space-y-2">
              {results.slice(0, 5).map((provider, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center gap-3">
                    <span className={clsx(
                      'flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold',
                      idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                    )}>
                      {idx + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{provider.name}</p>
                      <p className="text-xs text-gray-500">
                        {[provider.specialty, provider.city].filter(Boolean).join(' • ')}
                      </p>
                    </div>
                  </div>
                  {provider.matchScore && (
                    <span className="text-sm text-gray-600">{Math.round(provider.matchScore)}%</span>
                  )}
                </div>
              ))}
              {results.length > 5 && (
                <p className="text-xs text-gray-500 text-center pt-2">
                  + {results.length - 5} more providers
                </p>
              )}
            </div>
          )}

          {/* Metrics */}
          <div className="flex items-center gap-6 text-xs text-gray-500 pt-3 mt-3 border-t border-gray-100">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {(searchExecution.latencyMs || 0) / 1000}s
            </span>
            <span className="flex items-center gap-1">
              <DollarSign className="h-3.5 w-3.5" />
              ${(searchExecution.costUsd || 0).toFixed(4)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export function StabilityTestResults() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'summary' | 'iterations'>('summary');
  const fromEvaluations = searchParams.get('from') === 'evaluations';

  const { data: testRun, isLoading, error } = useQuery({
    queryKey: ['validation', 'test-run', id, 'stability-results'],
    queryFn: async () => {
      const response = await api.get<{ data: TestRun }>(`/api/validation/test-runs/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  // Fetch quality assessment results (Phase 5)
  const { data: qualityAssessmentData } = useQuery({
    queryKey: ['validation', 'quality-assessment-results', id],
    queryFn: async () => {
      const response = await api.qualityAssessment.getResults(id!);
      return response.data;
    },
    enabled: !!id && !!testRun?.isRevealed,
  });

  const completedExecutions = useMemo(() =>
    testRun?.executions.filter(e => e.status === 'COMPLETED') || [],
    [testRun?.executions]
  );

  const allSearchExecutions = useMemo(() =>
    completedExecutions.flatMap(e => e.searchExecutions || []).filter(se => se.status === 'COMPLETED'),
    [completedExecutions]
  );

  // Calculate Phase 2 metrics
  const phase2Metrics = useMemo(() => calculatePhase2Metrics(completedExecutions), [completedExecutions]);

  // Get evaluations
  const phase1Evaluation = testRun?.stabilityEvaluations.find(e => e.phase === 'PHASE_1');
  const phase2Evaluation = testRun?.stabilityEvaluations.find(e => e.phase === 'PHASE_2');

  // Model being tested
  const testedModel = testRun?.config.models[0];

  if (isLoading) return <LoadingState message="Loading stability results..." />;
  if (error) return <ErrorState message="Failed to load stability results" />;
  if (!testRun) return <ErrorState message="Test run not found" />;

  const hasPhase2 = testRun.searchPhaseStatus === 'EVALUATED' || testRun.searchPhaseStatus === 'COMPLETED';

  // Breadcrumb items - adjust based on navigation source
  const breadcrumbItems = fromEvaluations
    ? [
        { label: 'Model Validation', href: '/validation', icon: Home },
        { label: 'Evaluations', href: '/validation/evaluations', icon: ClipboardCheck },
        { label: testRun.name },
        { label: 'Results', icon: BarChart3 },
      ]
    : [
        { label: 'Model Validation', href: '/validation', icon: Home },
        { label: 'Test Runs', href: '/validation/test-runs', icon: Play },
        { label: testRun.name, href: `/validation/test-runs/${id}` },
        { label: 'Results', icon: BarChart3 },
      ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate(fromEvaluations ? '/validation/evaluations' : `/validation/test-runs/${id}`)}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">Results: {testRun.name}</h1>
              <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700">
                <Repeat className="h-3 w-3" />
                Stability Test
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                <Eye className="h-3 w-3" />
                Revealed
              </span>
            </div>
            <p className="text-sm text-gray-500">
              Test Case: {testRun.testCase.name} ({testRun.testCase.code}) • {completedExecutions.length} iterations
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1">
            <button
              onClick={() => setViewMode('summary')}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                viewMode === 'summary'
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-600 hover:bg-gray-50'
              )}
            >
              <Columns className="h-4 w-4" />
              Summary
            </button>
            <button
              onClick={() => setViewMode('iterations')}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                viewMode === 'iterations'
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-600 hover:bg-gray-50'
              )}
            >
              <List className="h-4 w-4" />
              Iterations
            </button>
          </div>
        </div>
      </div>

      {/* Model Being Tested Banner */}
      <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 p-6">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-amber-600 uppercase tracking-wide">Model Under Test</span>
            <div className="mt-2 flex items-center gap-3">
              {testedModel && <ModelBadge model={testedModel} />}
              <span className="text-sm text-gray-600">
                {testRun.config.iterations} iterations executed
              </span>
            </div>
          </div>
          <div className="text-right">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Test Case</span>
            <p className="mt-1 text-sm font-medium text-gray-900">{testRun.testCase.code}</p>
            <p className="text-xs text-gray-600">{testRun.testCase.condition}</p>
          </div>
        </div>
      </div>

      {/* Overall Stability Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Phase 1 Verdict */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Stethoscope className="h-5 w-5 text-amber-600" />
              <h2 className="text-lg font-semibold text-gray-900">Phase 2+3: Case Analysis</h2>
            </div>
            {phase1Evaluation && (
              <StabilityVerdictBadge verdict={phase1Evaluation.verdict} />
            )}
          </div>

          {phase1Evaluation ? (
            <div className="space-y-4">
              {phase1Evaluation.verdictReasoning && (
                <p className="text-sm text-gray-600">{phase1Evaluation.verdictReasoning}</p>
              )}
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-500">Evaluated by:</span>
                <span className="font-medium text-gray-900">{phase1Evaluation.evaluator.name}</span>
                {phase1Evaluation.completedAt && (
                  <span className="text-gray-500">
                    {format(new Date(phase1Evaluation.completedAt), 'MMM d, yyyy')}
                  </span>
                )}
              </div>
              <Link
                to={`/validation/test-runs/${testRun.id}/stability-evaluate`}
                className="inline-flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium"
              >
                <Eye className="h-4 w-4" />
                View full evaluation
              </Link>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" />
              <p>No evaluation submitted yet</p>
              <Link
                to={`/validation/test-runs/${testRun.id}/stability-evaluate`}
                className="mt-2 inline-flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium"
              >
                Submit evaluation
              </Link>
            </div>
          )}
        </div>

        {/* Phase 2 Verdict */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Search className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Phase 4: Provider Matching</h2>
            </div>
            {phase2Evaluation && (
              <StabilityVerdictBadge verdict={phase2Evaluation.verdict} />
            )}
          </div>

          {hasPhase2 ? (
            phase2Evaluation ? (
              <div className="space-y-4">
                {phase2Evaluation.verdictReasoning && (
                  <p className="text-sm text-gray-600">{phase2Evaluation.verdictReasoning}</p>
                )}
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-500">Evaluated by:</span>
                  <span className="font-medium text-gray-900">{phase2Evaluation.evaluator.name}</span>
                  {phase2Evaluation.completedAt && (
                    <span className="text-gray-500">
                      {format(new Date(phase2Evaluation.completedAt), 'MMM d, yyyy')}
                    </span>
                  )}
                </div>
                <Link
                  to={`/validation/test-runs/${testRun.id}/search-stability-evaluate`}
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  <Eye className="h-4 w-4" />
                  View full evaluation
                </Link>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                <p>No evaluation submitted yet</p>
                <Link
                  to={`/validation/test-runs/${testRun.id}/search-stability-evaluate`}
                  className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Submit evaluation
                </Link>
              </div>
            )
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Layers className="h-8 w-8 mx-auto mb-2" />
              <p>Phase 2 not yet executed</p>
            </div>
          )}
        </div>
      </div>

      {/* Consistency Metrics */}
      {viewMode === 'summary' && (
        <>
          {/* Phase 1 Metrics - AI Analysis */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <LLMConsistencyAnalysis testRunId={testRun.id} summaryOnly />
          </div>

          {/* Phase 2 Metrics */}
          {hasPhase2 && allSearchExecutions.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <Phase2ConsistencyMetrics metrics={phase2Metrics} />
            </div>
          )}
        </>
      )}

      {/* Iterations View */}
      {viewMode === 'iterations' && (
        <>
          {/* Phase 1 Iterations */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-4">
              <Stethoscope className="h-5 w-5 text-amber-600" />
              <h2 className="text-lg font-semibold text-gray-900">Phase 1 Iterations</h2>
              <span className="text-sm text-gray-500">({completedExecutions.length})</span>
            </div>
            <div className="space-y-3">
              {completedExecutions.map((execution, index) => (
                <IterationCard key={execution.id} execution={execution} index={index} />
              ))}
            </div>
          </div>

          {/* Phase 2 Iterations */}
          {hasPhase2 && allSearchExecutions.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="flex items-center gap-2 mb-4">
                <Search className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Phase 2 Iterations</h2>
                <span className="text-sm text-gray-500">({allSearchExecutions.length})</span>
              </div>
              <div className="space-y-3">
                {allSearchExecutions.map((searchExecution, index) => (
                  <SearchIterationCard key={searchExecution.id} searchExecution={searchExecution} index={index} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Performance Summary */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Performance Summary</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-lg bg-gray-50">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Iterations</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{completedExecutions.length}</p>
          </div>
          <div className="p-4 rounded-lg bg-gray-50">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Avg Latency (P1)</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {(completedExecutions.reduce((sum, e) => sum + (e.indicationLatencyMs || 0) + (e.criteriaLatencyMs || 0), 0) / completedExecutions.length / 1000).toFixed(1)}s
            </p>
          </div>
          <div className="p-4 rounded-lg bg-gray-50">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Cost (P1)</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              ${completedExecutions.reduce((sum, e) => sum + (e.indicationCostUsd || 0) + (e.criteriaCostUsd || 0), 0).toFixed(4)}
            </p>
          </div>
          <div className="p-4 rounded-lg bg-gray-50">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Search Iterations</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{allSearchExecutions.length}</p>
          </div>
        </div>
      </div>

      {/* Phase 5: Quality Assessment Results - Model Quality Summary */}
      {qualityAssessmentData && qualityAssessmentData.summary.completedSets > 0 && qualityAssessmentData.modelResults && qualityAssessmentData.modelResults.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <ClipboardCheck className="h-5 w-5 text-green-600" />
              Phase 5: Quality Assessment Results
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Quality of providers recommended by {testedModel ? MODEL_DISPLAY[testedModel]?.name || testedModel : 'the model'}
            </p>
            <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
              <span>{qualityAssessmentData.summary.completedSets} evaluation{qualityAssessmentData.summary.completedSets !== 1 ? 's' : ''}</span>
              <span>•</span>
              <span>{qualityAssessmentData.summary.totalAssessments} provider{qualityAssessmentData.summary.totalAssessments !== 1 ? 's' : ''} assessed</span>
              <span>•</span>
              <span>{qualityAssessmentData.summary.goodMatchCount} good match{qualityAssessmentData.summary.goodMatchCount !== 1 ? 'es' : ''}</span>
            </div>
          </div>

          {/* Model Quality Summary */}
          {qualityAssessmentData.modelResults[0] && (
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {testedModel && <ModelBadge model={testedModel} />}
                  <span className="text-sm text-gray-500">
                    {qualityAssessmentData.modelResults[0].totalProviders} provider{qualityAssessmentData.modelResults[0].totalProviders !== 1 ? 's' : ''} recommended
                  </span>
                </div>
                <div className={clsx(
                  'px-4 py-2 rounded-lg text-lg font-bold',
                  qualityAssessmentData.modelResults[0].avgScore >= 12 ? 'bg-green-100 text-green-700' :
                  qualityAssessmentData.modelResults[0].avgScore >= 8 ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-600'
                )}>
                  {qualityAssessmentData.modelResults[0].avgScore}/16 avg
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{qualityAssessmentData.modelResults[0].totalProviders}</p>
                  <p className="text-xs text-gray-500">Providers</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{qualityAssessmentData.modelResults[0].goodMatchCount}</p>
                  <p className="text-xs text-gray-500">Good Matches</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{Math.round(qualityAssessmentData.modelResults[0].goodMatchRate * 100)}%</p>
                  <p className="text-xs text-gray-500">Good Match Rate</p>
                </div>
              </div>

              {/* Dimension Score Breakdown */}
              <div className="grid grid-cols-6 gap-2">
                <div className="text-center p-2 bg-white rounded border border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Medical Fit</p>
                  <p className={clsx(
                    'text-sm font-bold',
                    qualityAssessmentData.modelResults[0].avgDimensionScores.medicalFit >= 2 ? 'text-green-600' : 'text-red-600'
                  )}>
                    {qualityAssessmentData.modelResults[0].avgDimensionScores.medicalFit}/3
                  </p>
                </div>
                <div className="text-center p-2 bg-white rounded border border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Urgency</p>
                  <p className={clsx(
                    'text-sm font-bold',
                    qualityAssessmentData.modelResults[0].avgDimensionScores.urgencyTiming >= 2 ? 'text-green-600' : 'text-red-600'
                  )}>
                    {qualityAssessmentData.modelResults[0].avgDimensionScores.urgencyTiming}/3
                  </p>
                </div>
                <div className="text-center p-2 bg-white rounded border border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Pathway</p>
                  <p className={clsx(
                    'text-sm font-bold',
                    qualityAssessmentData.modelResults[0].avgDimensionScores.therapyPathway >= 2.5 ? 'text-green-600' : 'text-red-600'
                  )}>
                    {qualityAssessmentData.modelResults[0].avgDimensionScores.therapyPathway}/3
                  </p>
                </div>
                <div className="text-center p-2 bg-white rounded border border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Complexity</p>
                  <p className="text-sm font-bold text-gray-700">
                    {qualityAssessmentData.modelResults[0].avgDimensionScores.complexityRisk}/2
                  </p>
                </div>
                <div className="text-center p-2 bg-white rounded border border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Outcome</p>
                  <p className={clsx(
                    'text-sm font-bold',
                    qualityAssessmentData.modelResults[0].avgDimensionScores.outcomeFit >= 2 ? 'text-green-600' : 'text-red-600'
                  )}>
                    {qualityAssessmentData.modelResults[0].avgDimensionScores.outcomeFit}/3
                  </p>
                </div>
                <div className="text-center p-2 bg-white rounded border border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Patient</p>
                  <p className="text-sm font-bold text-gray-700">
                    {qualityAssessmentData.modelResults[0].avgDimensionScores.patientCentricity}/2
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Back to Dashboard */}
      <div className="flex justify-center pt-4">
        <Link
          to={`/validation/test-runs/${testRun.id}`}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          Back to Test Run Dashboard
        </Link>
      </div>
    </div>
  );
}
